import { useState, useCallback, useEffect } from 'react';
import useAuth from 'Modules/firebase/use-auth';
import { UpdateUserInput } from '@/operator/services/graphql/mutations/updateUser';
import { useNotificationContext } from '@/contexts/NotificationContext';
import {
    FirestoreUser,
    useUpdateFirestoreUserMutation,
} from 'generated/graphql';

export const useUpdateUser = (notification = true) => {
    const { showNotification } = useNotificationContext();
    const { getAccessTokenSilently } = useAuth();

    const [data, setData] = useState<Partial<FirestoreUser> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const [updateFirestoreUser] = useUpdateFirestoreUserMutation();

    const updateUser = useCallback(
        async (id: string, input: UpdateUserInput) => {
            setLoading(true);
            setError(null);

            try {
                const response = await updateFirestoreUser({
                    variables: {
                        where: { id },
                        input,
                    },
                });

                if (response.errors) {
                    throw new Error(
                        response.errors[0]?.message || 'Unknown error occurred',
                    );
                }

                setData(response.data.updateFirestoreUser);

                if (notification) {
                    showNotification({
                        message:
                            'Updates to your profile have been successful.',
                        severity: 'success',
                    });
                }
            } catch (err) {
                console.error('Error updating user:', err);
                setError(err);
                showNotification({
                    message: 'Updates to your profile failed.',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        },
        [getAccessTokenSilently, notification, showNotification],
    );

    return {
        updateUser,
        updateUserLoading: loading,
        updateUserError: error,
        updateUserData: data,
    };
};
